// markup/clipboard.mjs — DropPin markup cut / copy / paste (pure, headless).
// ---------------------------------------------------------------------------
// Pure transforms over an objects array + a selection. No React / DOM / system
// clipboard — the React hook holds the in-app clipboard payload these return.
// Paste re-ids every object (so pasted copies are independent) and nudges them
// by a small normalised offset so a paste lands visibly off its source.
// ---------------------------------------------------------------------------

import { cloneObject, translateObject, genId as defaultGenId } from './model.mjs';

export const PASTE_OFFSET = 0.02; // normalised nudge (~2% of the drawing)

function idSet(ids) {
  return new Set((ids || []).map((x) => String(x)));
}

// Snapshot of the selected objects (independent clones), preserving z-order.
export function copyObjects(objects, ids) {
  const set = idSet(ids);
  return objects.filter((o) => set.has(String(o.id))).map((o) => cloneObject(o, o.id));
}

// Copy + remove: returns { remaining, clipboard }.
export function cutObjects(objects, ids) {
  const set = idSet(ids);
  const clipboard = objects.filter((o) => set.has(String(o.id))).map((o) => cloneObject(o, o.id));
  const remaining = objects.filter((o) => !set.has(String(o.id)));
  return { remaining, clipboard };
}

// Append re-ided, offset copies of the clipboard. genId injectable for tests.
export function pasteObjects(objects, clipboard, opts = {}) {
  const dx = opts.dx == null ? PASTE_OFFSET : opts.dx;
  const dy = opts.dy == null ? PASTE_OFFSET : opts.dy;
  const genId = opts.genId || defaultGenId;
  const pasted = (clipboard || []).map((o) => translateObject(cloneObject(o, genId()), dx, dy));
  return { objects: [...objects, ...pasted], pasted, pastedIds: pasted.map((p) => p.id) };
}
